import { useToastMessage } from 'ccs-ui-components';
import { TFunction } from 'i18next';

import useLoader from '../../../../common/hooks/Loader';
import { ExpressionBuilderData } from '../../../_components/expression-builder/utilities/TypeDefs';
import { FlowConnector, FlowItem, StepItem } from '../../../_components/flow-builder/TypeDefs';
import { AssociatedService, EventSchemaDetail, IngressEvent, JourneyComments, Service, ServiceOperation } from '../../../store/_api/TypeDefs';
import { ExternalEventConfirmationData } from '../../../_components/external-event-confirmation-modal/utilities/TypeDefs';

/**
 * Represents the selected flow item.
 * @interface
 */
export interface SelectedFlowItem {
  /**
   * The ID of the node.
   * @type {string}
   */
  nodeId: string;

  /**
   * The service data of the flow item.
   * @type {ServiceCommonData}
   */
  serviceData: ServiceCommonData;
}

/**
 * Represents the journey data.
 * @interface
 */
export interface JourneyData {
  /**
   * The name of the journey.
   * @type {string}
   */
  journeyName: string;

  /**
   * The description of the journey.
   * @type {string}
   */
  journeyDescription: string;

  /**
   * The user who updated the journey.
   * @type {string}
   */
  updatedBy: string;

  /**
   * The event map details of the journey.
   * @type {EventMapDetails}
   */
  eventMapDetails: EventMapDetails;

  /**
   * Indicates whether to update the version of the journey.
   * @type {boolean}
   */
  updateVersion: boolean;
}

/**
 * Represents the event map details.
 * @interface
 */
export interface EventMapDetails {
  eventMapName?: string;
  /**
   * The maximum execution time in seconds for the event map.
   * @type {number | undefined}
   */
  eventMapMaxExecutionTimeInSec?: number;

  /**
   * The unique identifier for the event map.
   * @type {string | undefined}
   */
  uniqueIdentifier?: string;

  /**
   * The nodes in the event map.
   * @type {EventMapNode[]}
   */
  eventMapNodes: EventMapNode[];

  /**
   * The edges in the event map.
   * @type {EventMapEdge[]}
   */
  eventMapEdges: EventMapEdge[];
}

/**
 * Represents a node in the event map.
 * @interface
 */
export interface EventMapNode {
  /**
   * The name of the event.
   * @type {string}
   */
  eventName: string;

  /**
   * The ID of the node.
   * @type {string}
   */
  nodeId: string;

  /**
   * The status of the node.
   * @type {string}
   */
  status: string;

  /**
   * The name of the service operation.
   * @type {string}
   */
  serviceOperationName: string;

  /**
   * The name of the service.
   * @type {string}
   */
  serviceName: string;

  /**
   * The description of the service.
   * @type {string}
   */
  serviceDescription: string;

  /**
   * The type of the service.
   * @type {string}
   */
  serviceType: string;

  /**
   * The x-coordinate of the node.
   * @type {number}
   */
  xCoordinate: number;

  /**
   * The y-coordinate of the node.
   * @type {number}
   */
  yCoordinate: number;

  /**
   * Indicates whether the action is a compensate action.
   * @type {boolean}
   */
  isCompensateAction: boolean;

  /**
   * The name of the compensatory event map.
   * @type {string | undefined}
   */
  compensatoryEventMapName?: string;

  /**
   * The details of the compensatory event map.
   * @type {CompensatoryEventMapData | undefined}
   */
  compensatoryEventMapDetails?: CompensatoryEventMapData;

  /**
   * The version of the event.
   * @type {number | undefined}
   */
  eventVersion?: number;

  /**
   * The version of the service.
   * @type {number | undefined}
   */
  serviceVersion?: number;
}

/**
 * Represents an edge in the event map.
 * @interface
 */
export interface EventMapEdge {
  /**
   * The ingress nodes of the edge.
   * @type {{ nodeId: string }[]}
   */
  ingress: { nodeId: string }[];

  /**
   * The egress nodes of the edge.
   * @type {{ nodeId: string, mep?: string, external: boolean, conditionalExpression?: string, rawConditionalExpression?: string }[]}
   */
  egress: {
    nodeId: string,
    mep?: string,
    external: boolean,
    nextStep?: string,
    conditionMap?: {
      [key: string]: {
        builderExpression: string,
        rawExpression: string
      }
    },
  }[];

  /**
   * The ingress operation of the edge.
   * @type {string | undefined}
   */
  ingressOperation?: string;

  /**
   * The payload transformations of the edge.
   * @type {{ egress: string, ingress: string, persistentAttributes: string[] }[] | undefined}
   */
  payloadTransformations?: {
    egress: string,
    ingress: string,
    persistentAttributes: string[]
  }[];
}

/**
 * Represents the common data for a service.
 * @interface
 */
export interface ServiceCommonData {
  nodeId: string;

  /**
   * The name of the service.
   * @type {string}
   */
  serviceName: string;

  /**
   * The description of the service.
   * @type {string}
   */
  serviceDescription: string;

  /**
   * The name of the service operation.
   * @type {string}
   */
  serviceOperationName: string;

  /**
   * The name of the ingress event.
   * @type {string}
   */
  ingressEvent: string;

  /**
   * The type of the service.
   * @type {string}
   */
  serviceType: string;

  /**
   * Indicates whether the service is external.
   * @type {boolean}
   */
  isExternal: boolean;

  /**
   * Indicates whether the action is a compensate action.
   * @type {boolean}
   */
  isCompensateAction: boolean;

  /**
   * The name of the compensatory event map.
   * @type {string | undefined}
   */
  compensatoryEventMapName?: string;

  /**
   * Indicates whether the service is saved.
   * @type {boolean}
   */
  isSaved: boolean;

  condExpr: {
    [key: string]: {
      /**
   * The conditional expression for the service.
   * @type {string | undefined}
   */
      rawExpression: string;

      /**
       * The conditional expression builder string for the service.
       * @type {string | undefined}
       */
      builderExpression: string;
    }
  },
  ingressEventVersion: number;
  serviceVersion: number;

  /**
  * The external value.
  * @type {[key: string]: boolean}
  */
  external: {
    [key: string]: boolean
  }
}

/**
 * Represents the data map for flow items.
 * @interface
 */
export interface FlowItemDataMap {
  /**
   * The mapping of flow item IDs to their respective service common data.
   * @type {Object.<string, ServiceCommonData>}
   */
  [key: string]: ServiceCommonData;
}

export interface EvenSchemaDetailMap {
  /**
   * The mapping of event schema name@version to their respective event schema detail.
   * @type {Object.<string, EventSchemaDetail>}
   */
  [key: string]: EventSchemaDetail;
}

/**
 * Represents the journey data for a step.
 * @interface
 * @extends FlowBuilderData
 */
export interface StepJourneyData extends FlowBuilderData {
  /**
   * The name of the journey step.
   * @type {string}
   */
  journeyStepName: string;

  /**
   * The description of the journey step.
   * @type {string}
   */
  journeyStepDescription: string;

  /**
   * The maximum execution time for the journey step.
   * @type {number}
   */
  maxExecutionTime: number;

  /**
   * The unique identifiers for the journey step.
   * @type {string[] | undefined}
   */
  uniqueIdentifiers?: string[];

  /**
   * The starting node of the journey step.
   * @type {FlowItem | undefined}
   */
  startingNode?: FlowItem;

  /**
   * The action of the journey step.
   * @type {JourneyStepAction}
   */
  journeyStepAction: JourneyStepAction;
  eventMapName?: string;
}

/**
 * Represents the mapping of step IDs to their respective journey data.
 * @interface
 */
export interface StepJourneyDataMap {
  /**
   * The mapping of step IDs to their respective journey data.
   * @type {Object.<string, StepJourneyData>}
   */
  [key: string]: StepJourneyData;
}

export enum DraggingItemType {
  SERVICE = 'SERVICE',
  ASSOCIATED_SERVICE = 'ASSOCIATED_SERVICE',
  NONE = ''
}

export enum SidebarState {
  ALL_SERVICES = 'ALL_SERVICES',
  INGRESS_EVENTS = 'INGRESS_EVENTS',
  ASSOCIATED_SERVICES = 'ASSOCIATED_SERVICES',
  RETURNED_COMMENT_HISTORY = 'RETURNED_COMMENT_HISTORY',
  NONE = ''
}

export enum SidebarDataLoadingState {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  NONE = ''
}

export enum JourneyStepAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  NONE = ''
}

/**
 * Represents the data for a flow builder.
 * @interface
 */
export interface FlowBuilderData {
  /**
   * The flow items in the flow builder.
   * @type {FlowItem[]}
   */
  flowItems: FlowItem[];

  /**
   * The flow connectors in the flow builder.
   * @type {FlowConnector[]}
   */
  flowConnectors: FlowConnector[];

  /**
   * The ID of the ingress node in the flow builder.
   * @type {string}
   */
  ingressNodeId: string;

  /**
   * Indicates whether the root node is external.
   * @type {boolean}
   */
  isRootExternal: boolean;
}

/**
 * Represents the data for a compensatory event map.
 * @interface
 */
export interface CompensatoryEventMapData {
  /**
   * The ID of the first node in the compensatory event map.
   * @type {string}
   */
  firstNodeId: string;

  /**
   * The type of the first node in the compensatory event map.
   * @type {string}
   */
  firstNodeType: string;

  /**
   * The nodes in the compensatory event map.
   * @type {EventMapNode[]}
   */
  eventMapNodes: EventMapNode[];

  /**
   * The edges in the compensatory event map.
   * @type {EventMapEdge[]}
   */
  eventMapEdges: EventMapEdge[];
}

/**
 * Represents the state of the journey canvas(for combining all states).
 * @interface
 */
export interface JourneyCanvasState {
  /**
   * The list of all services.
   * @type {Service[]}
   */
  allServices: Service[];

  /**
   * The filtered list of services.
   * @type {Service[] | undefined}
   */
  filteredServices?: Service[];

  /**
   * The mapping of event schema name@version to their respective event schema detail.
   * @type {Object.<string, EventSchemaDetail>}
   */
  eventSchemaMap: EvenSchemaDetailMap;

  /**
   * The currently selected event schema detail.
   * @type {EventSchemaDetail | undefined}
   */
  selectedEventSchema?: EventSchemaDetail;

  /**
   * The search term for filtering services.
   * @type {string}
   */
  searchTerm: string;

  /**
   * The selected connector in the flow builder.
   * @type {{ to: string; from: string }}
   */
  selectedConnector: { to: string; from: string };

  /**
   * The size state of the flow builder.
   * @type {{ width: number; height: number }}
   */
  flowBuilderSizeState: { width: number; height: number };

  /**
   * The selected flow item.
   * @type {SelectedFlowItem | undefined}
   */
  selectedFlowItem?: SelectedFlowItem;

  /**
   * The list of flow items in the flow builder.
   * @type {FlowItem[]}
   */
  flowItems: FlowItem[];

  /**
   * The list of step items in the flow builder.
   * @type {StepItem[]}
   */
  stepItems: StepItem[];

  /**
   * The list of deleted step items in the flow builder.
   * @type {StepItem[]}
   */
  deletedStepItems: StepItem[];

  /**
   * The list of flow connectors in the flow builder.
   * @type {FlowConnector[]}
   */
  flowConnectors: FlowConnector[];

  /**
   * The list of step connectors in the flow builder.
   * @type {FlowConnector[]}
   */
  stepConnectors: FlowConnector[];

  /**
   * The list of deleted step connectors in the flow builder.
   * @type {FlowConnector[]}
   */
  deletedStepConnectors: FlowConnector[];

  /**
   * The dragging item in the flow builder.
   * @type {{ item: Service | AssociatedService | null; startOffset: { x: number; y: number }; itemType: DraggingItemType }}
   */
  draggingItem: {
    item: Service | AssociatedService | null;
    serviceOperation?: ServiceOperation;
    ingressEvent?: IngressEvent;
    startOffset: { x: number; y: number };
    itemType: DraggingItemType;
  };

  /**
   * The data map for flow items.
   * @type {FlowItemDataMap}
   */
  flowItemDataMap: FlowItemDataMap;

  /**
   * The copy of the flow item data map.
   * @type {FlowItemDataMap | undefined}
   */
  flowItemDataMapCopy?: FlowItemDataMap;

  /**
   * The status of the sidebar.
   * @type {SidebarState}
   */
  sidebarState: SidebarState;

  /**
   * The list of service operations.
   * @type {ServiceOperation[]}
   */
  serviceOperations: ServiceOperation[];

  /**
   * The name of the journey.
   * @type {string}
   */
  journeyName: string;

  /**
   * The name of the journey step.
   * @type {string}
   */
  journeyStepName: string;

  /**
   * The description of the journey.
   * @type {string}
   */
  journeyDescription: string;

  /**
   * The description of the journey step.
   * @type {string}
   */
  journeyStepDescription: string;

  /**
   * The status of the journey.
   * @type {string}
   */
  journeyStatus: string;

  /**
   * The version of the journey.
   * @type {number}
   */
  journeyVersion: number;

  /**
   * The category of the journey.
   * @type {string}
   */
  journeyCategory: string;

  /**
   * The ID of the ingress node in the flow builder.
   * @type {string}
   */
  ingressNodeId: string;

  /**
   * Indicates whether the save journey button is enabled.
   * @type {boolean}
   */
  saveJourneyBtnEnabled: boolean;

  /**
   * Indicates whether the active journey is enabled.
   * @type {boolean}
   */
  enableActiveJourney: boolean;

  /**
   * Indicates whether the start node is selected.
   * @type {boolean}
   */
  startSelected: boolean;

  /**
   * The list of associated services.
   * @type {AssociatedService[]}
   */
  associatedServices: AssociatedService[];

  /**
   * The state of the sidebar data loading.
   * @type {SidebarDataLoadingState}
   */
  sidebarDataLoadingState: SidebarDataLoadingState;

  /**
   * Indicates whether the journey version is being updated.
   * @type {boolean}
   */
  updateVersion: boolean;

  /**
   * The data for loading all services.
   * @type {{ loadingMore: boolean; page: number; noLoadMore: boolean; failed: boolean }}
   */
  allServicesDataLoad: {
    loadingMore: boolean;
    page: number;
    noLoadMore: boolean;
    failed: boolean;
  };

  /**
   * Indicates whether the root node is external.
   * @type {boolean}
   */
  isRootExternal: boolean;

  /**
   * To keep parent journey stored while designing compensatory event map
   * @type {FlowBuilderData | undefined}
   */
  parentJourney?: FlowBuilderData;

  /**
   * The ID of the root node in the compensatory event map.
   * @type {string | undefined}
   */
  cemRootNodeId?: string;

  /**
   * The name of the root node in the compensatory event map.
   * @type {string | undefined}
   */
  cemRootNodeName?: string;

  /**
   * Indicates whether the compensatory event map is being designed.
   * @type {boolean}
   */
  designingCEM: boolean;

  /**
   * The status of the notify dialog.
   * @type {NotifyDialogStatus | undefined}
   */
  notifyDialogStatus?: NotifyDialogStatus;

  /**
   * Indicates whether the notify dialog is shown.
   * @type {boolean}
   */
  showNotifyDialog: boolean;

  /**
   * The ID of the node being deleted.
   * @type {string | undefined}
   */
  deletingNodeId?: string;

  /**
   * The name of the compensatory event map being deleted.
   * @type {string | undefined}
   */
  deletingCEMName?: string;

  /**
   * Indicates whether the journey step modal is shown.
   * @type {boolean}
   */
  showJourneyStepModal: boolean;

  /**
   * The input for the maximum execution time.
   * @type {{ hours: string; minutes: string; seconds: string; }}
   */
  maxExecutionTimeInput: { hours: string; minutes: string; seconds: string; };

  /**
   * The maximum execution time in seconds.
   * @type {number}
   */
  maxExecutionTime: number;

  /**
   * The list of unique identifiers.
   * @type {string[] | undefined}
   */
  uniqueIdentifiers?: string[];

  /**
   * The connector being deleted.
   * @type {{ from: string; to: string; } | undefined}
   */
  deletingConnector?: { from: string; to: string; };

  /**
   * The compensatory event map being designed.
   * @type {CompensatoryEventMap | undefined}
   */
  compensatoryEventMap?: CompensatoryEventMap;

  /**
   * Indicates whether there is an API error notification.
   * @type {boolean}
   */
  notifyApiError: boolean;

  /**
   * Indicates whether a step is being designed.
   * @type {boolean}
   */
  designingStep: boolean;

  /**
   * Indicates whether the initiate step card is visible.
   * @type {boolean}
   */
  initiateStepCardVisible: boolean;
  validation: { sidebarInvalidSearch: boolean; };
  designingInitialStepJourney: boolean;

  /**
   * The mapping of step IDs to their respective journey data.
   * @type {StepJourneyDataMap}
   */
  stepJourneyDataMap: StepJourneyDataMap;

  /**
   * The UUID of the current step card.
   * @type {string}
   */
  stepCardUUID: string;

  /**
   * The UUID of the previous step card.
   * @type {string}
   */
  prevStepCardUUID: string;

  /**
   * The selected endpoint flow item.
   * @type {FlowItem | undefined}
   */
  selectedEndpointFlowItem?: FlowItem;

  /**
   * The values for the dropdown endpoint.
   * @type {{ value: string; name: string; }[]}
   */
  dropdownEndpointValues: {
    value: string;
    name: string;
  }[];

  /**
    * The state of the step card menu dropdown.
    * @type {{ isVisible: boolean; xCoordinate: number; yCoordinate: number; }}
    */
  stepCardMenuDropdown: {
    isVisible: boolean;
    xCoordinate: number;
    yCoordinate: number;
  };

  /**
   * Indicates whether a journey has been created.
   * @type {boolean}
   */
  createdJourney: boolean;

  /**
   * Indicates whether the scheduler modal is shown.
   * @type {boolean}
   */
  schedulerModal: boolean;

  /**
   * Indicates whether the transform payload modal is shown.
   * @type {boolean}
   */
  showTransformPayloadModal: boolean;

  /**
   * The dropdown values for services payload.
   * @type {{ value: string; name: string; }[]}
   */
  dropDownServicesPayload: {
    value: string;
    name: string;
  }[];

  /**
   * The payload transformations.
   * @type {PayloadTransformation[]}
   */
  payloadTransformations: PayloadTransformation[];

  /**
   * The copy of the payload transformations.
   * @type {PayloadTransformation[] | undefined}
   */
  payloadTransformationsCopy?: PayloadTransformation[];

  /**
   * The ID of the node for payload transformation.
   * @type {string}
   */
  payloadTransformationNodeId: string;

  /**
   * Indicates whether the view journey has been initiated.
   * @type {boolean | undefined}
   */
  viewJourneyInitiated?: boolean;

  /**
   * The action of the journey step.
   * @type {JourneyStepAction}
   */
  journeyStepAction: JourneyStepAction;

  /**
   * Indicates whether the save journey to design CEM modal is shown.
   * @type {boolean}
   */
  saveJourneyToDesignCEMModal: boolean;

  /**
   * The event name for payload transformation.
   * @type {string}
   */
  payloadTransformationEventName: string;

  payloadTransformationEventVersion: number;

  /**
   * Indicates whether the CEM save button is enabled.
   * @type {boolean}
   */
  cemSaveBtn: boolean;

  /**
   * The initial data for the compensatory event map.
   * @type {CompensatoryEventMap | undefined}
   */
  initialCemData?: CompensatoryEventMap;

  /**
   * The initial condExpr data for the compensatory event map.
   * @type {FlowItemDataMap | undefined}
   */
  initialCemCondExprData?: FlowItemDataMap;
  showCondExpModal: boolean;

  /**
   * The data for the conditional expression.
   * @type {ExpressionBuilderData | undefined}
   */
  condExpData?: ExpressionBuilderData;

  isJourneyCreated: boolean;

  journeyType: string;

  journeyUuid?: string;

  journeyComments?: JourneyComments[];

  showExternalOnSubsequentStepModal: boolean;

  showExternalEventConfirmationModal: boolean;

  /**
  * The data for external value.
  * @type {ExternalEventConfirmationData | undefined}
  */
  externalEventConfirmationData?: ExternalEventConfirmationData;
}

/**
 * Represents the detail of a configuration.
 * @interface
 */
export interface ConfigDetailType {
  /**
   * The status of the configuration.
   * @type {string}
   */
  status: string;

  /**
   * The name of the configuration.
   * @type {string}
   */
  configName: string;

  /**
   * The error message associated with the configuration.
   * @type {string}
   */
  errorMessage: string;

  /**
   * The source configuration version.
   * @type {number}
   */
  srcConfigVersion: number;

  /**
   * The array of configurations.
   * @type {Array<unknown> | undefined}
   */
  configs?: Array<unknown>;
}

/**
 * Represents a configuration.
 * @interface
 */
export interface ConfigType {
  /**
   * The count of configurations.
   * @type {number}
   */
  configsCount: number;

  /**
   * The type of the configuration.
   * @type {string}
   */
  configType: string;

  /**
   * The details of the configuration.
   * @type {ConfigDetailType[]}
   */
  configDetails: ConfigDetailType[];
}

/**
 * Represents the payload for the configuration response data.
 * @interface
 */
export interface ConfigResponsedataPayload {
  /**
   * The correlation ID of the configuration.
   * @type {string}
   */
  correlationId: string;

  /**
   * The total count of configurations.
   * @type {number}
   */
  totalConfigsCount: number;

  /**
   * The source environment of the configuration.
   * @type {string}
   */
  sourceEnv: string;

  /**
   * The user who created the configuration.
   * @type {string}
   */
  createdBy: string;

  /**
   * The timestamp when the configuration was created.
   * @type {string}
   */
  createdTimestamp: string;

  /**
   * The array of configurations.
   * @type {ConfigType[]}
   */
  configs: ConfigType[];
}

/**
 * Represents a payload transformation.
 * @interface
 */
export interface PayloadTransformation {
  /**
   * The egress value.
   * @type {string}
   */
  egress: string;
  /**
   * The ingress value.
   * @type {string}
   */
  ingress: string;
  /**
   * The persistent attributes.
   * @type {string[]}
   */
  persistentAttributes: string[];
}

/**
 * Represents a compensatory event map.
 * @interface
 */
export interface CompensatoryEventMap {
  /**
   * The compensatory event map name.
   * @type {string|undefined}
   */
  compensatoryEventMapName?: string;
  /**
   * The ingress node ID.
   * @type {string}
   */
  ingressNodeId: string;
  /**
   * The flow items.
   * @type {FlowItem[]}
   */
  flowItems: FlowItem[];
  /**
   * The flow connectors.
   * @type {FlowConnector[]}
   */
  flowConnectors: FlowConnector[];
  /**
   * The payload transformations.
   * @type {PayloadTransformation[]}
   */
  payloadTransformations?: PayloadTransformation[];
}

export enum ServiceTypes {
  BUSINESS = 'business',
  EXTERNAL = 'external',
  MQ = 'mq',
  DB2 = 'db2',
  SOAP = 'soap'
}

export enum FlowItemButtonActions {
  EDIT_CEM = 'EDIT_CEM',
  DELETE_ITEM = 'DELETE_ITEM',
  CREATE_CEM = 'CREATE_CEM',
  TRANSFORM_PAYLOAD = 'TRANSFORM_PAYLOAD',
}

export enum NotifyDialogStatus {
  DELETE_CEM = 'DELETE_CEM',
  DELETE_NODE = 'DELETE_ITEM',
  DELETE_CONNECTOR = 'DELETE_LINE',
  DELETE_STEP = 'DELETE_STEP',
  DELETE_SUBSEQUENT_STEP = 'DELETE_SUBSEQUENT_STEP'
}

export enum FlowConnectorButtonActions {
  DELETE = 'DELETE',
  COND_EXP = 'COND_EXP',
  EXTERNAL = 'EXTERNAL',
  VIEW_EVENT_SCHEMA = 'VIEW_EVENT_SCHEMA'
}

export enum FlowItemConditions {
  AND = 'AND',
  OR = 'OR',
  T_AND = 'T-AND'
}

export interface PayloadFieldType {
  name: string;
  type: PayloadFieldType;
  items?: PayloadFieldType[];
  fields?: PayloadFieldType[];
}

export type LoaderHook = ReturnType<typeof useLoader>;

export type ToastMessageHook = ReturnType<typeof useToastMessage>;

export type TranslateHook = TFunction<'translation', undefined, 'translation'>
